import React, { useContext } from "react";

import { AnalyticsContext } from "@brexhq/next/src/contexts/Analytics";
import {
  UserSoftSignupEvent,
  UserEarlyAccSoftSignupEvent,
} from "@brexhq/next/src/telemetry/analyticsEvents";
import { logAnalyticsError } from "@brexhq/next/src/telemetry/logging";
import { AnalyticsApi, AnalyticsEvent } from "@brexhq/next/src/telemetry/types";
import type { SignedupUserProperties } from "@brexhq/next/src/utils/signup";

export const createTrackSignupSuccess =
  (api: AnalyticsApi, earlyAccount = false): TrackSignupSuccess =>
  (userId: string, userProperties: SignedupUserProperties) => {
    /**
     * Very important these calls happen in this order,
     * otherwise we end up sending analytics with null user ids!
     */
    api.identify({ id: userId, properties: userProperties });
    if (earlyAccount) {
      api.trackEvent(new UserEarlyAccSoftSignupEvent(userProperties));
    } else {
      api.trackEvent(new UserSoftSignupEvent(userProperties));
    }
  };

const useAnalytics = () => {
  const {
    analyticsApi,
    state: { eventLog },
  } = useContext(AnalyticsContext);

  const trackEvent = React.useCallback(
    (event: AnalyticsEvent) => {
      try {
        analyticsApi.trackEvent(event);
      } catch (error) {
        if (error instanceof Error) {
          logAnalyticsError(error);
        }
      }
    },
    [analyticsApi],
  );

  /**
   * It is crucial that the calls are made in this exact order.
   * Otherwise analytics events will be missing the user id that is persisted by the identify call
   */
  const trackSignupSuccess = React.useMemo(
    () => createTrackSignupSuccess(analyticsApi),
    [analyticsApi],
  );

  const trackEarlyAccountSignupSuccess = React.useMemo(
    () => createTrackSignupSuccess(analyticsApi, true),
    [analyticsApi],
  );

  return {
    ...analyticsApi,
    trackEvent,
    trackSignupSuccess,
    trackEarlyAccountSignupSuccess,
    eventLog,
  };
};

export default useAnalytics;

export type UseAnalyticsHookApi = ReturnType<typeof useAnalytics>;

export type TrackSignupSuccess = (
  userId: string,
  userProperties: SignedupUserProperties,
) => void;
